// src/components/BusinessLoan.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './BusinessLoan.css';

const BusinessLoan = () => {
  const navigate = useNavigate();
  const [loanAmount, setLoanAmount] = useState(1000000);
  const [loanTenure, setLoanTenure] = useState(3);
  const [businessAge, setBusinessAge] = useState(3);
  const [annualTurnover, setAnnualTurnover] = useState(5000000);
  const [activeTab, setActiveTab] = useState('calculator');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    city: ''
  });

  const handleGoBack = () => {
    navigate('/home');
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    alert('Thank you! Our financial advisors will contact you within 24 hours.');
  };

  const calculateEMI = () => {
    const principal = loanAmount;
    const rate = 0.12; // 12% annual interest
    const time = loanTenure * 12;
    const monthlyRate = rate / 12;
    const emi = (principal * monthlyRate * Math.pow(1 + monthlyRate, time)) / 
                (Math.pow(1 + monthlyRate, time) - 1);
    return Math.round(emi);
  };

  const calculateTotalInterest = () => {
    const emi = calculateEMI();
    return (emi * loanTenure * 12) - loanAmount;
  };

  const lenders = [
    { name: "HDFC Bank", rate: "10.99%", processing: "0.5%", tenure: "1-5 years", logo: "🏦", minAmount: "₹10 Lakhs", maxAmount: "₹5 Crores" },
    { name: "ICICI Bank", rate: "11.25%", processing: "0.75%", tenure: "1-7 years", logo: "🏛️", minAmount: "₹5 Lakhs", maxAmount: "₹10 Crores" },
    { name: "Axis Bank", rate: "11.50%", processing: "0.5%", tenure: "1-4 years", logo: "🏢", minAmount: "₹2 Lakhs", maxAmount: "₹3 Crores" },
    { name: "Bajaj Finserv", rate: "12.00%", processing: "1%", tenure: "1-8 years", logo: "🛡️", minAmount: "₹1 Lakh", maxAmount: "₹5 Crores" },
  ];

  const businessLoanTypes = [
    { title: "Working Capital", description: "Bridge cash flow gaps and manage operational expenses", amount: "₹50,000 - ₹2 Cr", tenure: "1-3 years", icon: "📈", features: ["No collateral", "Quick approval", "Flexible repayment"] },
    { title: "Term Loan", description: "Long-term funding for expansion and asset purchase", amount: "₹5 Lakhs - ₹5 Cr", tenure: "1-10 years", icon: "🏢", features: ["Competitive rates", "Custom tenure", "Bulk disbursement"] },
    { title: "Equipment Finance", description: "Purchase machinery, vehicles, and business equipment", amount: "₹10 Lakhs - ₹3 Cr", tenure: "3-7 years", icon: "⚙️", features: ["Asset backed", "Tax benefits", "Equipment as collateral"] }
  ];

  const benefits = [
    { icon: "⚡", title: "Fast Processing", desc: "Approval in 24-72 hours" },
    { icon: "📊", title: "High Loan Amount", desc: "Up to ₹5 Crores" },
    { icon: "🛡️", title: "Collateral Free", desc: "No security required" },
    { icon: "💳", title: "Flexible Repayment", desc: "Customized EMI options" },
    { icon: "🎯", title: "Low Interest", desc: "Starting at 10.99%" },
    { icon: "📝", title: "Minimal Docs", desc: "Quick documentation" }
  ];

  const steps = [
    { number: "01", title: "Apply Online", desc: "Fill 2-minute application" },
    { number: "02", title: "Get Approval", desc: "Instant eligibility check" },
    { number: "03", title: "Submit Documents", desc: "Upload required docs" },
    { number: "04", title: "Receive Funds", desc: "Amount disbursed in 72h" }
  ];

  return (
    <div className="bl-container">
      {/* Navigation Bar */}
      <nav className="bl-nav">
        <div className="bl-nav-container">
          {/* <button className="bl-back-button" onClick={handleGoBack}>
            <span className="bl-back-arrow">←</span>
            <span>Back</span>
          </button> */}
          <div className="bl-nav-logo">
            <span className="bl-logo-icon">🏢</span>
            <span className="bl-logo-text">Business Finance</span>
          </div>
          <div className="bl-nav-contact">
            <span className="bl-phone-icon">📞</span>
            <span>9909002220</span>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="bl-hero-section">
        <div className="bl-hero-overlay"></div>
        <div className="bl-hero-content">
          <div className="bl-hero-text">
            <h1 className="bl-hero-title">
              Fuel Your Business Growth with <span className="bl-gradient-text">Smart Financing</span>
            </h1>
            <p className="bl-hero-subtitle">
              Access ₹50,000 to ₹5 Crores from 50+ lenders. Compare rates, choose the best offer.
            </p>
            <div className="bl-hero-stats">
              <div className="bl-stat-item">
                <div className="bl-stat-number">50+</div>
                <div className="bl-stat-label">Lenders</div>
              </div>
              <div className="bl-stat-divider"></div>
              <div className="bl-stat-item">
                <div className="bl-stat-number">₹5Cr</div>
                <div className="bl-stat-label">Max Loan</div>
              </div>
              <div className="bl-stat-divider"></div>
              <div className="bl-stat-item">
                <div className="bl-stat-number">72h</div>
                <div className="bl-stat-label">Disbursal</div>
              </div>
              <div className="bl-stat-divider"></div>
              <div className="bl-stat-item">
                <div className="bl-stat-number">0%</div>
                <div className="bl-stat-label">Prepayment</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="bl-main-content">
        {/* Calculator & Form Section */}
        <section className="bl-calculator-section">
          <div className="bl-section-header">
            <h2>Calculate Your Business Loan</h2>
            <p>Get instant estimates with our smart calculator</p>
          </div>

          <div className="bl-calculator-container">
            <div className="bl-calculator-sidebar">
              <div className="bl-calculator-tabs">
                <button 
                  className={`bl-tab-btn ${activeTab === 'calculator' ? 'bl-active' : ''}`}
                  onClick={() => setActiveTab('calculator')}
                >
                  <span className="bl-tab-icon">🧮</span>
                  <span>EMI Calculator</span>
                </button>
                <button 
                  className={`bl-tab-btn ${activeTab === 'apply' ? 'bl-active' : ''}`}
                  onClick={() => setActiveTab('apply')}
                >
                  <span className="bl-tab-icon">📋</span>
                  <span>Apply Now</span>
                </button>
              </div>

              {activeTab === 'calculator' ? (
                <div className="bl-calculator-content">
                  <div className="bl-input-group">
                    <label>Loan Amount</label>
                    <div className="bl-amount-display">
                      <span className="bl-amount-currency">₹</span>
                      <span className="bl-amount-value">
                        {(loanAmount / 100000).toFixed(1)} Lakhs
                      </span>
                    </div>
                    <input
                      type="range"
                      min="50000"
                      max="50000000"
                      step="50000"
                      value={loanAmount}
                      onChange={(e) => setLoanAmount(parseInt(e.target.value))}
                      className="bl-range-slider"
                    />
                    <div className="bl-range-labels">
                      <span>₹50K</span>
                      <span>₹1Cr</span>
                      <span>₹5Cr</span>
                    </div>
                  </div>

                  <div className="bl-input-group">
                    <label>Tenure (Years)</label>
                    <div className="bl-tenure-options">
                      {[1, 2, 3, 5, 7].map((year) => (
                        <button
                          key={year}
                          className={`bl-tenure-btn ${loanTenure === year ? 'bl-active' : ''}`}
                          onClick={() => setLoanTenure(year)}
                        >
                          {year} Year{year > 1 ? 's' : ''}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="bl-input-group">
                    <label>Annual Turnover</label>
                    <select 
                      value={annualTurnover}
                      onChange={(e) => setAnnualTurnover(parseInt(e.target.value))}
                      className="bl-select-input"
                    >
                      <option value="1000000">₹10 Lakhs</option>
                      <option value="5000000">₹50 Lakhs</option>
                      <option value="10000000">₹1 Crore</option>
                      <option value="50000000">₹5 Crores</option>
                    </select>
                  </div>

                  <div className="bl-calculation-results">
                    <div className="bl-result-card">
                      <div className="bl-result-label">Monthly EMI</div>
                      <div className="bl-result-value">₹{calculateEMI().toLocaleString()}</div>
                    </div>
                    <div className="bl-result-card">
                      <div className="bl-result-label">Total Interest</div>
                      <div className="bl-result-value">₹{calculateTotalInterest().toLocaleString()}</div>
                    </div>
                    <div className="bl-result-card">
                      <div className="bl-result-label">Total Payment</div>
                      <div className="bl-result-value">
                        ₹{(calculateEMI() * loanTenure * 12).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <form className="bl-application-form" onSubmit={handleSubmit}>
                  <div className="bl-form-grid">
                    <div className="bl-form-group">
                      <label>Full Name</label>
                      <input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="John Smith" required />
                    </div>
                    <div className="bl-form-group">
                      <label>Email Address</label>
                      <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="john@business.com" required />
                    </div>
                    <div className="bl-form-group">
                      <label>Phone Number</label>
                      <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="+91 98765 43210" required />
                    </div>
                    <div className="bl-form-group">
                      <label>Company Name</label>
                      <input type="text" name="company" value={formData.company} onChange={handleInputChange} placeholder="Your Business Name" required />
                    </div>
                  </div>
                  <button type="submit" className="bl-submit-btn">
                    <span>Get Free Quotes</span>
                    <span className="bl-submit-arrow">→</span>
                  </button>
                </form>
              )}
            </div>

            <div className="bl-calculator-features">
              <h3>Key Benefits</h3>
              <div className="bl-benefits-grid">
                {benefits.map((benefit, index) => (
                  <div key={index} className="bl-benefit-card">
                    <div className="bl-benefit-icon">{benefit.icon}</div>
                    <div className="bl-benefit-content">
                      <h4>{benefit.title}</h4>
                      <p>{benefit.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Loan Types */}
        <section className="bl-loan-types-section">
          <div className="bl-section-header">
            <h2>Tailored Business Financing</h2>
            <p>Choose the perfect loan for your business needs</p>
          </div>
          <div className="bl-loan-types-grid">
            {businessLoanTypes.map((loan, index) => (
              <div key={index} className="bl-loan-type-card">
                <div className="bl-loan-icon-wrapper">
                  <span className="bl-loan-icon">{loan.icon}</span>
                </div>
                <h3>{loan.title}</h3>
                <p className="bl-loan-description">{loan.description}</p>
                <div className="bl-loan-details">
                  <div className="bl-detail">
                    <span className="bl-detail-label">Amount</span>
                    <span className="bl-detail-value">{loan.amount}</span>
                  </div>
                  <div className="bl-detail">
                    <span className="bl-detail-label">Tenure</span>
                    <span className="bl-detail-value">{loan.tenure}</span>
                  </div>
                </div>
                <div className="bl-loan-features">
                  {loan.features.map((feature, idx) => (
                    <div key={idx} className="bl-feature-tag">
                      <span className="bl-check-icon">✓</span>
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Lender Comparison */}
        <section className="bl-lenders-section">
          <div className="bl-section-header">
            <h2>Compare Top Lenders</h2>
            <p>Get the best rates from India's leading financial institutions</p>
          </div>
          <div className="bl-lenders-grid">
            {lenders.map((lender, index) => (
              <div key={index} className="bl-lender-card">
                <div className="bl-lender-header">
                  <div className="bl-lender-logo">{lender.logo}</div>
                  <div className="bl-lender-info">
                    <h3>{lender.name}</h3>
                    <div className="bl-interest-badge">
                      <span className="bl-percent-icon">%</span>
                      <span>{lender.rate}</span>
                    </div>
                  </div>
                </div>
                <div className="bl-lender-details">
                  <div className="bl-detail-row">
                    <span className="bl-detail-label">Processing Fee</span>
                    <span className="bl-detail-value">{lender.processing}</span>
                  </div>
                  <div className="bl-detail-row">
                    <span className="bl-detail-label">Loan Tenure</span>
                    <span className="bl-detail-value">{lender.tenure}</span>
                  </div>
                  <div className="bl-detail-row">
                    <span className="bl-detail-label">Min Amount</span>
                    <span className="bl-detail-value">{lender.minAmount}</span>
                  </div>
                  <div className="bl-detail-row">
                    <span className="bl-detail-label">Max Amount</span>
                    <span className="bl-detail-value">{lender.maxAmount}</span>
                  </div>
                </div>
                <button className="bl-apply-btn">
                  Apply Now
                  <span className="bl-apply-arrow">→</span>
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Process Steps */}
        <section className="bl-process-section">
          <div className="bl-section-header">
            <h2>Simple 4-Step Process</h2>
            <p>Get your business loan in just a few clicks</p>
          </div>
          <div className="bl-process-steps">
            {steps.map((step, index) => (
              <div key={index} className="bl-process-step">
                <div className="bl-step-number">{step.number}</div>
                <div className="bl-step-content">
                  <h3>{step.title}</h3>
                  <p>{step.desc}</p>
                </div>
                {index < steps.length - 1 && (
                  <div className="bl-step-connector">
                    <div className="bl-connector-line"></div>
                    <span className="bl-connector-arrow">→</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="bl-cta-section">
          <div className="bl-cta-content">
            <h2>Ready to Grow Your Business?</h2>
            <p>Get personalized loan offers within minutes</p>
            <div className="bl-cta-buttons">
              <button className="bl-cta-primary">
                Apply Now
                <span className="bl-cta-arrow">→</span>
              </button>
              <button className="bl-cta-secondary">
                <span className="bl-phone-icon-cta">📞</span>
                Talk to Advisor
              </button>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bl-footer">
        <div className="bl-footer-content">
          <div className="bl-footer-section">
            <h4>Business Loans</h4>
            <ul>
              <li>Working Capital</li>
              <li>Term Loans</li>
              <li>Equipment Finance</li>
              <li>Startup Loans</li>
            </ul>
          </div>
          <div className="bl-footer-section">
            <h4>Contact Us</h4>
            <div className="bl-contact-info">
              <div className="bl-contact-item">
                <span className="bl-contact-icon">📞</span>
                <span>9909002220</span>
              </div>
              <div className="bl-contact-item">
                <span className="bl-contact-icon">🕐</span>
                <span>Mon-Sat: 9AM-9PM</span>
              </div>
            </div>
          </div>
        </div>
        <div className="bl-footer-bottom">
          <p>© 2024 Krishna Finconcept Services. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default BusinessLoan;